import { cloneElement } from 'react';
import {
  Tabs as ChakraTabs,
  TabList,
  TabPanel,
  TabPanels,
  Tab,
  TabProps as ChakraTabProps,
  TabPanelProps as ChakraTabPanelProps,
  TabsProps as ChakraTabsProps,
  BoxProps,
} from '@chakra-ui/react';
import { ComponentStyleConfig } from '@chakra-ui/theme';
import { specialFont, primaryThemeGradient } from 'constants/theme';

export type TabOption = {
  label: string;
  panel: React.ReactElement | React.ElementType;
  tabComponent?: React.ReactElement;
};

export type TabsProps = Omit<ChakraTabsProps, 'children'> & {
  size?: 'md';
  options: TabOption[];
  tabProps?: ChakraTabProps;
  panelProps?: ChakraTabPanelProps;
  tabListProps?: BoxProps;
  children?: React.ReactNode;
};

const TabsStyles: ComponentStyleConfig = {
  baseStyle: {
    tab: {
      textAlign: 'center',
      _focus: {
        boxShadow: 'none',
      },
    },
  },
  sizes: {
    md: {
      fontSize: 'md',
      letterSpacing: 'wider',
      py: 3,
    },
  },
  variants: {
    solid: {
      tab: {
        bg: 'black',
        color: 'primaryFontColour.400',
        fontWeight: 'medium',
        fontFamily: specialFont,
        borderRadius: 0,
        _selected: {
          bg: primaryThemeGradient,
        },
      },
    },
    line: {
      tab: {
        letterSpacing: 3,
        fontFamily: specialFont,
        _selected: {
          color: 'primaryThemeColour.400',
        },
      },
    },
  },
};

const Tabs = ({
  options,
  tabProps = {},
  panelProps = {},
  size = 'md',
  variant = 'solid',
  tabListProps,
  ...props
}: TabsProps) => {
  const isDynatect = window.location.href.includes('dynatect');
  const renderTabs = options.map(({ label, tabComponent: TabComponent }) => (
    <Tab key={label} {...(isDynatect ? { bg: '#BABCBF' } : {})} {...tabProps}>
      {TabComponent ? cloneElement(TabComponent) : label}
    </Tab>
  ));
  const renderPanels = options.map(({ label, panel: Panel }) => {
    const renderPanel = typeof Panel === 'function' ? <Panel /> : cloneElement(Panel as React.ReactElement);

    return (
      <TabPanel key={label} {...panelProps}>
        {renderPanel}
      </TabPanel>
    );
  });

  return (
    <ChakraTabs size={size} variant={variant} {...props}>
      <TabList {...tabListProps}>{renderTabs}</TabList>
      <TabPanels>{renderPanels}</TabPanels>
    </ChakraTabs>
  );
};

export { Tabs, TabsStyles };
